import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, matchRoutes, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAlarmContentList } from '../features/notice/noticeSlice';
import { logout } from "../features/admin/adminSlice";
import { formatDate } from '../utils/formatDate';
import AOS from "aos";
import useToken from "../utils/useToken";
import "aos/dist/aos.css";
import "../css/remixicon/remixicon.css";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { useRef } from "react";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const Header = () => {
  const [isSitemapOpen, setIsSitemapOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const routes = [
    { path: "/" },
    { path: "/login" },
    { path: "/about" },
    { path: "/solution" },
    { path: "/tech" },
    { path: "/notice" },
    { path: "/notice/:noticeId" },
    { path: "/privacy" },
    { path: "/notice/content" },
    { path: "/notice/content/:noticeId" },
  ];
  
  const matched = matchRoutes(routes, location);
  const isNotFound = !matched;
  
  const isMain = location.pathname === "/" || location.pathname === "/login"||isNotFound;
  const isNotice = location.pathname.startsWith("/notice");
  const is404 = location.pathname === "/404";
  const admin = useSelector((state) => state.admin.admin);

  const [openAlram, setOpenAlram] = useState(false);

  const categories = useSelector((state) => state.notice.categories || []);
  const alarmContentList = useSelector((state) => state.notice.alarmContentList);
  const periodDate = useSelector((state) => state.setting.settings.noticePeriod);
  const totalLatestCount = useSelector((state) => state.notice.totalLatestCount);
  const defaultCategory = { categoryId: 'NOTI', categoryName: '공지사항' };
  const [activeCategory, setActiveCategory] = useState(defaultCategory.categoryId);

  /**
  * @description 화면 오른쪽 메뉴바 여닫기
  */
  const toggleSitemap = () => {
    setIsSitemapOpen((prev) => !prev);
  };

  /**
  * @description 로그아웃
  */
  const handleLogout = async() => {
    await dispatch(logout());
    sessionStorage.removeItem("token");
    window.location.href = "/";
    toggleSitemap();
  };

  const isTokenExpired = useToken();
  
  /**
  * @description 각 페이지로 이동
  */
  function goToAbout(event) { event.preventDefault(); setIsSitemapOpen(false);navigate('/about'); }
  function goToSolution(event) { event.preventDefault(); setIsSitemapOpen(false);navigate('/solution'); }
  function goToTech(event) { event.preventDefault(); setIsSitemapOpen(false);navigate('/tech'); }
  function goToNews(event) { event.preventDefault(); setIsSitemapOpen(false);navigate('/notice', { state: { categoryID: 'NOTI' } }); }

  //#region 알림 모달창
  //모달 최신글 호출 api
  const endpoint = useMemo(() => {
    const ca = encodeURIComponent(String(activeCategory));
    return `/content/lastest?ca=${ca}&vs=list&pi=1`;
  }, [activeCategory]);
  
  const modalRef = useRef(null);
  const bellRef = useRef(null);

  // 여닫기
  const toggleAlarmModal = () => {
    setOpenAlram(prev => {
      const next = !prev;
  
      if (next) {
        if (categories && categories.length > 0) {
          setActiveCategory(String(categories[0].id));
        } else {
          setActiveCategory(defaultCategory.categoryId);
        }
      }
  
      return next;
    });
  };

  const handleCloseModal = () => {
    setActiveCategory(defaultCategory.categoryId);
    setOpenAlram(false);
  };

  // 카테고리 선택 및 리스트 필터링
  const handleFilter = (categoryId) => {
    setActiveCategory(String(categoryId));
  };

  const filteredList = useMemo(() => {
    const ac = String(activeCategory);
    return (alarmContentList || []).filter(item => String(item.category_id) === ac);
  }, [alarmContentList, activeCategory]);
  
  // 게시글 상세보기
  const goDetail = (noticeId) => {
    // console.log(noticeId)
    navigate(`/notice/${noticeId}`);
    handleCloseModal();
  };
  //#endregion

  //#region useEffect
  // 모달 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setOpenAlram(false);
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
  
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  //스크롤 이벤트 + AOS 초기화
  useEffect(() => {
    AOS.init();
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
      handleCloseModal();
    };

    window.addEventListener("scroll", handleScroll);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  //토큰 만료 시 로그아웃
  useEffect(() => {
    if (isTokenExpired) {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refreshToken");
      window.location.href = "/";
    }
  }, [isTokenExpired]);

  //카테고리 변경 시 알람 목록 요청
  useEffect(() => {
    if (openAlram && activeCategory) {
      dispatch(getAlarmContentList(endpoint));
    }
  }, [openAlram, activeCategory, endpoint, dispatch]);

  useEffect(() => {
  
      dispatch(getAlarmContentList(endpoint));

  }, []);
  //#endregion

  
  return (
    <>
      <header className={`header ${isMain ? "main" : ""} ${isScrolled ? "down bg" : ""}`}>
        <div className="inner">
          <h1 className="logo">
            <Link to="/"></Link>
          </h1>
          <div className="nav">
            <nav>
              <ul className="deps1">
                <li><a onClick={goToAbout} className={location.pathname === "/about" ? "active" : ""}>About</a></li>
                <li><a onClick={goToSolution} className={location.pathname === "/solution" ? "active" : ""}>Solution</a></li>
                <li><a onClick={goToTech} className={location.pathname === "/tech" ? "active" : ""}>Technology</a></li>
                <li><a onClick={goToNews}  className={location.pathname.startsWith("/notice") ? "active" : ""}>News</a></li>
              </ul>
            </nav>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
            <div className="bell-icon-container">
            {typeof totalLatestCount === 'number' && totalLatestCount > 0 && (
              <span className="new-badge">{totalLatestCount}</span>
            )}

              <button ref={bellRef} type="button" className="bell-icon-active" onClick={toggleAlarmModal}>
                <span className="sound_only">알림</span>
              </button>
            </div>

            {openAlram && (
              <div  ref={modalRef} className={`alramModal ${openAlram ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
                <h3 className="alarmTitle">WIZ NEWS</h3>
                {categories && (
                  <ul className="alarm_cate">
                        {/* <li
                          className={activeCategory === 'all' ? "alarm_cate_on" : ""}
                          onClick={() => handleFilter('all')}
                        >
                          전체
                        </li> */}
                       {categories.map((category) => (
                          <li
                            key={category.id}
                            className={activeCategory === category.id ? "bo_cate_on" : ""}
                            onClick={() => handleFilter(category.id)}
                          >
                            {category.name}
                          </li>
                        ))}
                    </ul>
                )}
                <div className="latest_list">
                  {filteredList.length > 0 ? (
                    filteredList.map((item) => {
                      const cat = categories.find(cat => String(cat.id) === String(item.category_id));
                      const categoryName = cat ? cat.name : '카테고리';
                      const noticePeriodDate = Date.now() - periodDate * 24 * 60 * 60 * 1000;
                      const isLatest = new Date(item.created_at).getTime() >= noticePeriodDate;
                      const preview = item.PREVIEW_YN;

                      return (
                        <div
                          key={item.id}
                          className="latest_item"
                          onClick={(e) => {
                            e.stopPropagation();
                            goDetail(item.id);
                          }}
                        >
                          <div className="latest_info">
                            <div className="status-container">
                              {preview === 'Y' && <p className="latest_pre">임시저장</p>}
                              {item.category_id && <span className="latest_cate">{categoryName}</span>}
                            </div>
                            <span className="latest_date">{formatDate(item.created_at)}</span>
                          </div>
                          <h3 className={`latest_subject ${isLatest ? 'icon_new' : ''}`}>{item.title}</h3>
                        </div>
                      );
                    })
                  ) : (
                    <div className="empty_table">게시물이 없습니다.</div>
                  )}
                </div>
              </div>
            )}

            <div className="sitemap">
              <button className="btn_sitemap" onClick={toggleSitemap}>
                <span className="bar1"></span>
                <span className="bar2"></span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {isSitemapOpen && (
        <div className="sitemap_container show" id="m_nav" onClick={toggleSitemap}>
          <div className="sitemap_contents" onClick={(event) => event.stopPropagation()}>
            <div className="sitemap_nav">
              <ul>
                <li><a onClick={goToAbout}>About</a></li>
                <li><a onClick={goToSolution}>Solution</a></li>
                <li><a onClick={goToTech}>Technology</a></li>
                <li><a onClick={goToNews}>News</a></li>
                {admin && (
                  <li>
                    <a onClick={handleLogout} className="logout-button">
                      로그아웃
                    </a>
                  </li>
                )}
              </ul>
              <div className="sitemap_download">
                <a
                  href="/resource/2025_wizfactory_introduction.pdf"
                  className="btn-download"
                >
                  회사소개서 다운로드 <i className="ri-arrow-down-line"></i>
                </a>
              </div>
            </div>
            <button type="button" className="sitemap_close" onClick={toggleSitemap}>
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {isNotice && !is404 &&  (
        <section className="page_title about">
          <div className="page_title_bg about"></div>
          <div className="page_title_text">
            <h2>
              WIZ <span style={{ fontWeight: 300 }}>NEWS</span>
            </h2>
            <p>위즈팩토리의 소식을 알려드립니다.</p>
            {/* {onlyNotice ? (
              <fieldset id="bo_sch">
                <legend>게시물 검색</legend>
                <form name="fsearch" method="get">
                  <input
                    type="text"
                    name="stx"
                    placeholder="검색어를 입력해주세요"
                    required
                    className="sch_input"
                    size="15"
                    maxLength="20"
                  />
                  <button type="submit" className="sch_btn">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <span className="sound_only">검색</span>
                  </button>
                </form>
              </fieldset>
            ):(
              <fieldset id="bo_sch"></fieldset>
            )} */}
          </div>
        </section>
      )}

      

    </>
  );
};

export default Header;
